/**
 * FormComponent - Web Component che emula HTMLFormElement
 * 
 * Risolve i problemi di nesting form creando un componente che:
 * - Implementa la stessa API di HTMLFormElement (checkValidity, reportValidity, reset)
 * - Gestisce eventi submit/reset tramite CustomEvent
 * - Supporta FormData integration
 * - Non è un vero <form> quindi può essere annidato ovunque
 * 
 * Usage:
 * <form-component role="my-form" data-action="add">
 *   <input name="field1" required>
 *   <button type="submit">Submit</button>
 * </form-component>
 */
export class FormComponent extends HTMLElement {
    static webComponentName: string = "form-component";
    
    private formControls: NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | null = null;

    constructor() {
        super();
        this.init();
    }

    private init(): void {
        // Intercetta eventi submit dai button/input type="submit"
        this.addEventListener('click', (event: Event) => {
            const target = event.target as HTMLElement;
            
            // Controlla se è un button type="submit" o input type="submit"
            if (this.isSubmitElement(target)) {
                event.preventDefault();
                this.handleSubmit(event);
            }
        });

        // Intercetta eventi keydown per Enter sui form controls
        this.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                const target = event.target as HTMLElement;
                
                // Se Enter è premuto su un form control, trigger submit
                if (this.isFormControl(target)) {
                    event.preventDefault();
                    this.handleSubmit(event);
                }
            }
        });
    }

    /**
     * Controlla se un elemento è un submit button/input
     */
    private isSubmitElement(element: HTMLElement): boolean {
        if (element.tagName === 'BUTTON') {
            const button = element as HTMLButtonElement;
            return button.type === 'submit' || !button.hasAttribute('type');
        }
        
        if (element.tagName === 'INPUT') {
            const input = element as HTMLInputElement;
            return input.type === 'submit';
        }
        
        return false;
    }

    /**
     * Controlla se un elemento è un form control
     */
    private isFormControl(element: HTMLElement): boolean {
        const formControlTags = ['INPUT', 'SELECT', 'TEXTAREA'];
        return formControlTags.includes(element.tagName) && 
               element.closest('form-component') === this;
    }

    /**
     * Gestisce l'evento submit
     */
    private handleSubmit(originalEvent: Event): void {
        // Crea un CustomEvent che emula l'evento submit di HTMLFormElement
        const submitEvent = new CustomEvent('submit', {
            bubbles: true,
            cancelable: true,
            detail: {
                originalEvent,
                formComponent: this
            }
        });

        // Dispatch dell'evento submit
        const isDefaultPrevented = !this.dispatchEvent(submitEvent);
        
        // Se preventDefault non è stato chiamato, potremmo fare azioni di default
        // (in questo caso non facciamo nulla, lasciamo al handler decidere)
    }

    /**
     * Ottiene tutti i form controls all'interno del componente
     */
    private getFormControls(): (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[] {
        if (!this.formControls) {
            this.formControls = this.querySelectorAll('input, select, textarea');
        }
        return Array.from(this.formControls).filter(control => 
            control.closest('form-component') === this
        ) as (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[];
    }

    /**
     * Refresh della cache dei form controls (chiamare dopo modifiche DOM)
     */
    public refreshFormControls(): void {
        this.formControls = null;
    }

    // =============================
    // HTMLFormElement API Emulation
    // =============================

    /**
     * Emula HTMLFormElement.checkValidity()
     * Verifica la validità di tutti i form controls
     */
    checkValidity(): boolean {
        const controls = this.getFormControls();
        
        for (const control of controls) {
            if (!control.checkValidity()) {
                return false;
            }
        }
        
        return true;
    }

    /**
     * Emula HTMLFormElement.reportValidity()
     * Mostra messaggi di validazione e restituisce validità
     */
    reportValidity(): boolean {
        const controls = this.getFormControls();
        let isValid = true;
        let firstInvalidControl: HTMLElement | null = null;
        
        for (const control of controls) {
            if (!control.checkValidity()) {
                control.reportValidity();
                isValid = false;
                // Salva il primo campo non valido per il focus
                if (!firstInvalidControl) {
                    firstInvalidControl = control;
                }
            }
        }
        
        // Focus sul primo campo non valido trovato
        if (firstInvalidControl) {
            firstInvalidControl.focus();
        }
        
        return isValid;
    }

    /**
     * Emula HTMLFormElement.reset()
     * Resetta tutti i form controls ai valori iniziali
     */
    reset(): void {
        const controls = this.getFormControls();
        
        // Dispatch evento reset (before reset)
        const resetEvent = new CustomEvent('reset', {
            bubbles: true,
            cancelable: true,
            detail: {
                formComponent: this
            }
        });
        
        const isDefaultPrevented = !this.dispatchEvent(resetEvent);
        
        if (!isDefaultPrevented) {
            // Resetta ogni form control
            controls.forEach(control => {
                if (control instanceof HTMLInputElement) {
                    switch (control.type) {
                        case 'checkbox':
                        case 'radio':
                            control.checked = control.defaultChecked;
                            break;
                        default:
                            control.value = control.defaultValue;
                            break;
                    }
                } else if (control instanceof HTMLSelectElement) {
                    // Reset delle select alle opzioni di default
                    Array.from(control.options).forEach(option => {
                        option.selected = option.defaultSelected;
                    });
                } else if (control instanceof HTMLTextAreaElement) {
                    control.value = control.defaultValue;
                }
                
                // Rimuove stati di validità personalizzati
                control.setCustomValidity('');
            });
        }
    }

    // =============================
    // FormData Integration
    // =============================

    /**
     * Supporto per new FormData(formComponent)
     * Implementa l'iteratore necessario per FormData
     */
    *[Symbol.iterator](): Iterator<[string, FormDataEntryValue]> {
        const controls = this.getFormControls();
        
        for (const control of controls) {
            const name = control.name;
            if (!name) continue;
            
            if (control instanceof HTMLInputElement) {
                if (control.type === 'radio' || control.type === 'checkbox') {
                    if (control.checked) {
                        yield [name, control.value];
                    }
                } else if (control.type === 'file') {
                    const files = control.files;
                    if (files && files.length > 0) {
                        for (const file of Array.from(files)) {
                            yield [name, file];
                        }
                    }
                } else {
                    yield [name, control.value];
                }
            } else if (control instanceof HTMLSelectElement) {
                if (control.multiple) {
                    for (const option of control.selectedOptions) {
                        yield [name, option.value];
                    }
                } else {
                    yield [name, control.value];
                }
            } else if (control instanceof HTMLTextAreaElement) {
                yield [name, control.value];
            }
        }
    }

    // =============================
    // Utility Methods
    // =============================

    /**
     * Ottiene tutti i dati del form come oggetto
     * Simile a Object.fromEntries(new FormData(form))
     */
    getFormData(): Record<string, any> {
        const data: Record<string, any> = {};
        const controls = this.getFormControls();
        
        // Gestisce anche i campi disabled (che FormData ignora)
        controls.forEach(control => {
            const name = control.name;
            if (!name) return;
            
            if (control instanceof HTMLInputElement) {
                if (control.type === 'radio' || control.type === 'checkbox') {
                    if (control.checked) {
                        data[name] = control.value;
                    }
                } else if (control.type === 'file') {
                    data[name] = control.files;
                } else {
                    data[name] = control.value;
                }
            } else if (control instanceof HTMLSelectElement) {
                if (control.multiple) {
                    data[name] = Array.from(control.selectedOptions).map(option => option.value);
                } else {
                    data[name] = control.value;
                }
            } else if (control instanceof HTMLTextAreaElement) {
                data[name] = control.value;
            }
        });
        
        return data;
    }

    /**
     * Override di querySelector per mantenere compatibilità con codice esistente
     */
    querySelector<K extends keyof HTMLElementTagNameMap>(selectors: K): HTMLElementTagNameMap[K] | null;
    querySelector<K extends keyof SVGElementTagNameMap>(selectors: K): SVGElementTagNameMap[K] | null;
    querySelector<E extends Element = Element>(selectors: string): E | null;
    querySelector(selectors: string): Element | null {
        return super.querySelector(selectors);
    }

    /**
     * Override di querySelectorAll per mantenere compatibilità
     */
    querySelectorAll<K extends keyof HTMLElementTagNameMap>(selectors: K): NodeListOf<HTMLElementTagNameMap[K]>;
    querySelectorAll<K extends keyof SVGElementTagNameMap>(selectors: K): NodeListOf<SVGElementTagNameMap[K]>;
    querySelectorAll<E extends Element = Element>(selectors: string): NodeListOf<E>;
    querySelectorAll(selectors: string): NodeListOf<Element> {
        return super.querySelectorAll(selectors);
    }

    // =============================
    // Lifecycle Callbacks
    // =============================

    connectedCallback(): void {
        // Refresh form controls quando il componente viene connesso
        this.refreshFormControls();
    }

    disconnectedCallback(): void {
        // Cleanup se necessario
        this.formControls = null;
    }

    // =============================
    // Static Registration Method
    // =============================

    /**
     * Registra il web component
     */
    static registerElement(): void {
        if (!customElements.get(this.webComponentName)) {
            customElements.define(this.webComponentName, this);
        } else {
            console.warn(`Element ${this.webComponentName} is already defined.`);
        }
    }
}